import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  SearchInput,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
  ColumnDef,
} from '@tonic-one/react-data-grid';
import { AddIcon, CloseSIcon } from '@tonic-one/react-icons';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DelModal from 'src/pages/Report/ActionModal/del';
import { RoleProps } from '@Types/administrator';
import CSpinner from '@Components/CSpinner';
import ErrorAlert from '@Components/Common/ErrorAlert';
import IconButton from '@Components/IconButton';
import SmartTooltip from '@Report/SmartTooltip';
import { useNavigate } from 'react-router-dom';
import { AdministrationApi } from '@Apis';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const Roles = () => {
  const queryClient = useQueryClient();
  const { isPending, data: rolesData } = useQuery({
    queryKey: ['getRoles'],
    queryFn: async () => {
      return (await AdministrationApi.getRoles()).data;
    },
  });
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const [delModal, setDelModal] = useState<{
    isOpen: boolean;
    title: string;
    content: JSX.Element[] | undefined;
  }>({
    isOpen: false,
    title: '',
    content: undefined,
  });
  const { widthEditablePermission } = usePermissionStore();
  const [searchKeyword, setSearchKeyword] = useState('');
  const filteredData = useMemo(() => {
    if (!searchKeyword) return rolesData;
    return rolesData?.filter((item) =>
      ['roleDescription', 'roleName'].some((field) =>
        item[field].toLowerCase().includes(searchKeyword.toLowerCase())
      )
    );
  }, [rolesData, searchKeyword]);

  const getRoleDisplayName = useCallback((role: RoleProps) => {
    return role.isDefault === true ? 'Global administrator' : role.roleName;
  }, []);

  const columns: ColumnDef[] = useMemo(
    () => [
      {
        ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
        width: '5%',
        // minWidth: 40,
      },
      {
        id: 'roleName',
        header: <SmartTooltip>{t('common.label.role')}</SmartTooltip>,
        accessorKey: 'roleName',
        sortingFn: (a, b) => {
          const roleA = a.row.value;
          const roleB = b.row.value;
          // Get the display names that users actually see
          const displayNameA = getRoleDisplayName(roleA);
          const displayNameB = getRoleDisplayName(roleB);
          return displayNameA.localeCompare(displayNameB, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
        width: '35%',
        // minWidth: 250,
        cell: ({ row }: { row: { value: RoleProps } }) => {
          const role = row.value;
          const displayName = getRoleDisplayName(role);
          const isDisabled =
            !widthEditablePermission(PermissionNameEnum.administrator) ||
            role.isDefault === true;

          return (
            <Link
              maxWidth="100%"
              onClick={() => {
                if (!isDisabled) {
                  navigate(
                    `/administration/user-management/edit-role/${role.roleId}`
                  );
                }
              }}
              disabled={isDisabled}
            >
              <SmartTooltip>{displayName}</SmartTooltip>
            </Link>
          );
        },
      },
      {
        id: 'roleDescription',
        header: <SmartTooltip>{t('common.label.description')}</SmartTooltip>,
        accessorKey: 'roleDescription',
        width: '45%',
        // minWidth: 500,
        cell: ({ row }: { row: { value: RoleProps } }) => (
          <SmartTooltip>{row.value.roleDescription}</SmartTooltip>
        ),
      },
      {
        id: 'userCount',
        header: <SmartTooltip>{t('common.label.member')}</SmartTooltip>,
        accessorKey: 'userCount',
        width: '15%',
        // minWidth: 250,
        cell: ({ row }: { row: { value: RoleProps } }) => (
          <SmartTooltip>{row.value.userCount}</SmartTooltip>
        ),
      },
    ],
    [t, navigate, widthEditablePermission, getRoleDisplayName]
  );
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const handleRowSelectionChange = useCallback(
    (newRowSelection: Record<string, boolean>) => {
      setRowSelection(newRowSelection);
    },
    []
  );

  const isRowSelectable = useCallback(
    ({ row }: { row: { value: RoleProps } }) => {
      const role = row.value;
      return !(
        role.isDefault === true ||
        role.userCount !== 0 ||
        !widthEditablePermission(PermissionNameEnum.administrator)
      );
    },
    [widthEditablePermission]
  );

  const getRowId = useCallback((row: RoleProps) => {
    return row.roleId;
  }, []);

  const selectedRowCount = Object.values(rowSelection).filter(Boolean).length;
  const handleDel = () => {
    const selectedRows = getSelectedRows();
    console.log(selectedRows);
    const content = selectedRows.map((item) => (
      <Text key={item.sha1} mb={8}>
        {item.roleName}
      </Text>
    ));
    setDelModal({
      isOpen: true,
      title: t('administration.rbac.deleteRole.alertMsg'),
      content,
    });
  };

  const reset = useCallback(() => {
    setRowSelection({});
    setSearchKeyword('');
  }, []);

  const getSelectedRows = useCallback(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    const selectedRows: RoleProps[] = [];
    for (const id of selectedIds) {
      const item = rolesData?.find((item) => item.roleId === id);
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }, [rowSelection, rolesData]);

  const deleteRole = useMutation({
    mutationFn: async (id: string) => AdministrationApi.deleteRoleByRoleId(id),
    onSuccess: () => {
      setDelModal({
        isOpen: false,
        title: '',
        content: undefined,
      });
      reset();
      window.showNotification({
        titleMsg: t('administration.rbac.deleteRole.successMsg'),
        severity: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['getRoles'] }).then((r) => r);
    },
    onError: () => {
      setDelModal({
        isOpen: false,
        title: '',
        content: undefined,
      });
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    },
  });

  const confirmDel = async () => {
    if (!selectedRowCount) return;
    const selectedRows = getSelectedRows();
    deleteRole.mutate(selectedRows[0].roleId);
  };
  return (
    <Flex flex={1}>
      {(isPending || deleteRole.isPending) && <CSpinner></CSpinner>}
      <Flex flex={1} direction="column">
        <DelModal
          visible={delModal.isOpen}
          onClose={() => {
            setDelModal({
              isOpen: false,
              title: '',
              content: undefined,
            });
          }}
          onConfirm={confirmDel}
          loading={deleteRole.isPending}
          title={delModal.title}
          content={delModal.content}
          withEditPermission={widthEditablePermission(
            PermissionNameEnum.administrator
          )}
        ></DelModal>
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex mx="3x" my="4x" alignItems="center">
          {selectedRowCount !== 0 ? (
            <Stack direction="row" gap="3x">
              <Button
                variant="secondary"
                onClick={handleDel}
                disabled={
                  !widthEditablePermission(PermissionNameEnum.administrator) ||
                  selectedRowCount > 1
                }
              >
                {t('reportCommon.buttons.delete')}
              </Button>
              <Flex alignItems="center">
                <IconButton onClick={reset}>
                  <Icon as={CloseSIcon}></Icon>
                </IconButton>
                <Box lineHeight="2rem">
                  <Text color={colorStyle.color.tertiary}>
                    {selectedRowCount}&nbsp;
                    {t('common.label.selected')}
                  </Text>
                </Box>
              </Flex>
            </Stack>
          ) : null}
          {selectedRowCount === 0 ? (
            <Stack direction="row" gap="3x">
              <Button
                variant="primary"
                onClick={() => {
                  navigate('/administration/user-management/add-role');
                }}
                disabled={
                  !widthEditablePermission(PermissionNameEnum.administrator)
                }
              >
                <Icon as={AddIcon} mr={8} />
                {t('common.action.label.add')}
              </Button>
              <SearchInput
                onChange={(e: any) => {
                  setSearchKeyword(e.target.value);
                }}
                onClearInput={() => {
                  setSearchKeyword('');
                }}
                width="374px"
              />
            </Stack>
          ) : null}
        </Flex>
        <Flex flex={1}>
          <DataGrid
            data={filteredData || []}
            columns={columns}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            isRowSelectable={isRowSelectable}
            getRowId={getRowId}
            isColumnResizable
            isColumnSortable
            autoSorting
            emptyContent={t('common.noData')}
            disableAutoResetRowSelection
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
export default Roles;
